import { fromEvent, merge, Observable } from 'rxjs';
import { Component, OnInit, OnDestroy, ElementRef, ViewChildren, HostListener } from '@angular/core';
import { ICompany } from './company.model';
import { FormGroup, FormBuilder, Validators, FormArray, FormControlName, FormControl, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AddressBookService } from './address-book.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyValidator } from './company-validator';
import { Guid } from '../shared/utils';
import { IAddressBookSearchData } from './address-book-search-data.model';
import { GenericValidator } from '../shared/generic-validator';
import { IContact } from './contact.model';
import { Location } from '@angular/common';
import { NotificationsService, Notification } from '../shared/helper-components/index';
import { GlobalErrorHandler } from '../shared/core/error-handler';
import { ModalPromptService, PromptOptions } from '../shared/helper-components/modal-prompt.service';
import { ComponentCanDeactivate } from '../shared/core';
import { AuthService } from '../shared/core/index';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';

@Component({
  selector: 'company-item',
  standalone: false,
  templateUrl: './company-item.component.html',
  styleUrls: ['./company-item.component.css']
})
export class CompanyItemComponent implements OnInit, ComponentCanDeactivate, OnDestroy {
  company: ICompany = new ICompany();
  companyForm: FormGroup;
  sub: Subscription;
  errorMessage: string = '';
  pageTitle: string = '';
  SaveLabel: string = 'Create';
  displayMessage: { [key: string]: string } = {};
  private validationMessages: { [key: string]: { [key: string]: string } };
  private genericValidator: GenericValidator;
  @ViewChildren(FormControlName, { read: ElementRef }) formInputElements: ElementRef[];
  currentCompanyContact: string = Guid.MakeNew().ToString();
  defaultCompanyContact: FormControl;

  constructor(
    private _addressBookService: AddressBookService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private location: Location,
    private router: Router,
    private _notificationsService: NotificationsService,
    private _errorHandler: GlobalErrorHandler,
    private _prompt: ModalPromptService,
    public _authService: AuthService,
  ) {
    this.validationMessages = {
      name: {
        required: 'Please Enter the Company Name.',
        maxlength: 'Company Name Cannot be Longer than 80 Characters',
        companyTaken: 'Company Name is Already Taken'
      },
      notes: {
        maxlength: 'Notes Cannot be Longer than 255 Characters'
      }
    };

    this.genericValidator = new GenericValidator(this.validationMessages);
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    if (this.promptSubscription)
      this.promptSubscription.unsubscribe();
  }

  ngAfterViewInit() {
    let controlBlurs: Observable<any>[] = this.formInputElements
      .map((formControl: ElementRef) => fromEvent(formControl.nativeElement, 'blur'));

    this.defaultCompanyContact.valueChanges.subscribe(data => {
      this.currentCompanyContact = data;
    });

    merge(this.companyForm.valueChanges, ...controlBlurs).pipe(
      debounceTime(500)
    ).subscribe(value => {
      this.displayMessage = this.genericValidator.processMessages(this.companyForm);
    });
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    let isFormClean = !this.companyForm.dirty;
    return isFormClean;
  }

  public ngOnInit() {
    this.companyForm = this.fb.group({
      id: '',
      name: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(80)], CompanyValidator.checkCompanyName()],
      notes: ['', [Validators.maxLength(255)]],
      contacts: this.fb.array([this.initCompanyContacts(this.currentCompanyContact)])
    });

    this.defaultCompanyContact = new FormControl();
    this.companyForm.addControl('defaultCompanyContact', this.defaultCompanyContact);
    this.companyForm.get('defaultCompanyContact').patchValue(this.currentCompanyContact);

    this.sub = this.route.params.subscribe(
      params => {
        let id = params['companyId'];
        if (id && id != '0' && id != '') {
          this.SaveLabel = 'Save Changes';
          this.getCompany(id);
        } else {
          this.pageTitle = 'New Company';
          this.resetCompany();
          this.SaveLabel = 'Create';
        }
      }
    );
  }

  get contacts(): FormArray {
    return <FormArray>this.companyForm.get('contacts');
  }

  resetCompany() {
    this.company = new ICompany();
    this.company.id = '';
    this.company.name = '';
    this.company.notes = '';
  }

  initCompanyContacts(primaryId: string = Guid.MakeNew().ToString()): FormGroup {
    return this.fb.group({
      id: primaryId,
      primaryc: primaryId,
      name: ['', [Validators.maxLength(80)]],
      contactTitle: ['', [Validators.maxLength(255)]],
      address: ['', [Validators.maxLength(255)]],
      address2: ['', [Validators.maxLength(255)]],
      city: ['', [Validators.maxLength(255)]],
      state: ['', [Validators.maxLength(255)]],
      zipCode: ['', [Validators.maxLength(100)]],
      country: ['', [Validators.maxLength(255)]],
      phoneNumber: ['', [Validators.maxLength(40)]],
      phoneNumber2: ['', [Validators.maxLength(40)]],
      phoneNumber3: ['', [Validators.maxLength(40)]],
      cellNumber: ['', [Validators.maxLength(40)]],
      faxNumber: ['', [Validators.maxLength(40)]],
      email: ['', [Validators.pattern('[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+')]],
      notes: ['', [Validators.maxLength(255)]],
      isDefault: false,
    });
  }

  resetDefaultRecord(control: FormArray, defaultField: string, currentIndex) {
    if (control.value[currentIndex].id === this.companyForm.get(defaultField).value) {
      if (control.length > 1) {
        if (currentIndex <= 0) {
          this.companyForm.get(defaultField).patchValue(control.value[currentIndex + 1].id);
        } else {
          this.companyForm.get(defaultField).patchValue(control.value[currentIndex - 1].id);
        }
      }
    }
  }

  saveCompany(): void {
    if (this.companyForm.dirty && this.companyForm.valid) {
      if (this.contacts.length == 1) {
        this.companyForm.get('defaultCompanyContact').patchValue(this.currentCompanyContact);
      }
      let p = <ICompany>Object.assign({}, this.companyForm.value);
      var isNew: boolean = (p.id === '' || p.id === '0');

      this._addressBookService.saveCompany(p)
        .subscribe(
          (p) => { this.onSaveComplete(p, isNew); },
          (error: any) => {
            var e = JSON.stringify(error);
            if (e.indexOf('AntiXssMiddleware') !== -1 || error.includes('AntiXssMiddleware')) {
              this._notificationsService.add(new Notification('error', this._errorHandler.xssMessage));
            } else {
              this._errorHandler.processError(error);
            }
          }
        );
    } else if (!this.companyForm.dirty) {
      this.onSaveComplete(null, false);
    }
  }

  onSaveComplete(newCompany: ICompany, isNew: boolean): void {
    if (!newCompany) {
      return;
    }

    this.companyForm.markAsPristine();
    this.router.navigate(['addressbook', newCompany.id]);
    this._notificationsService.add(new Notification('success', newCompany.name + ' was ' + (isNew ? 'added' : 'updated') + ' successfully'));
  }

  addCompanyContact(): void {
    let newId = Guid.MakeNew().ToString();
    let newCc = this.initCompanyContacts(newId);
    this.contacts.push(newCc);
    if (this.contacts.length <= 1) {
      this.companyForm.get('defaultCompanyContact').patchValue(newId);
    }
    this.companyForm.markAsDirty();
  }

  removeCompanyContact(i: number): void {
    const control = <FormArray>this.contacts;
    this.resetDefaultRecord(control, 'defaultCompanyContact', i);
    control.removeAt(i);
    this.companyForm.markAsDirty();
  }

  CompanyContactSelected(selectedItem) {
    this.currentCompanyContact = selectedItem.value.id;
    this.companyForm.controls.defaultCompanyContact.setValue(selectedItem.value.id);
  }

  isNewCompany(): boolean {
    return this.company.id && this.company.id != '0';
  }

  getCompany(id: string): void {
    var searchValue = new IAddressBookSearchData();
    searchValue.companyId = id;
    this._addressBookService.getCompany(searchValue)
      .subscribe(
        (company: IAddressBookSearchData) => this.onCompanyRetrieved(company),
        (error: any) => {
          this._errorHandler.processError(error);
        }
      );
  }

  onCompanyRetrieved(data: IAddressBookSearchData): void {
    let contacts = <FormArray>this.companyForm.controls['contacts'];

    if (this.companyForm) {
      this.companyForm.reset();
      this.company.id = '';
      this.company.name = '';
      this.company.notes = '';
      this.currentCompanyContact = Guid.MakeNew().ToString();
      contacts.controls.pop();
    }

    if (!data || !data.resultSet) {
      this.errorMessage = 'Could not find company. It may have been deleted (or deactivated)!';
      return;
    }

    this.refreshCompany(data.resultSet, contacts);
  }

  validDefaultContact(companyFound: ICompany): string {
    if (typeof companyFound.defaultContact === 'undefined') {
      return this.currentCompanyContact;
    } else {
      return companyFound.defaultContact.id ? companyFound.defaultContact.id : this.currentCompanyContact;
    }
  }

  refreshCompany(companiesFound: ICompany[], contacts: FormArray) {
    var companyFound = companiesFound[0];
    this.company.id = companyFound.id;
    this.company.name = companyFound.name;
    this.company.notes = companyFound.notes;

    this.pageTitle = `Company: ${this.company.name}`;

    this.companyForm.patchValue({
      id: this.company.id,
      name: this.company.name,
      notes: this.company.notes,
      defaultCompanyContact: companyFound.defaultContact.id ? companyFound.defaultContact.id : this.currentCompanyContact,
    });

    if (companyFound.contacts.length == 0) {
      let defaultPC = new IContact();
      defaultPC.id = this.currentCompanyContact;
      companyFound.contacts.push(defaultPC);
    }

    companiesFound.forEach(comp => {
      comp.contacts.forEach(c => {
        const fb = this.initCompanyContacts();
        fb.patchValue({
          id: c.id,
          name: c.name,
          contactTitle: c.contactTitle,
          address: c.address,
          address2: c.address2,
          city: c.city,
          state: c.state,
          zipCode: c.zipCode,
          country: c.country,
          phoneNumber: c.phoneNumber,
          phoneNumber2: c.phoneNumber2,
          phoneNumber3: c.phoneNumber3,
          cellNumber: c.cellNumber,
          faxNumber: c.faxNumber,
          email: c.email,
          notes: c.notes,
          isDefault: c.isDefault
        });
        if (!c.isDefault) {
          contacts.push(fb);
        } else {
          contacts.insert(0, fb);
        }
      });

      while (this.contacts.value.findIndex(x => x.id === null) !== -1) {
        var index = this.contacts.value.findIndex(x => x.id === null);
        this.contacts.removeAt(index);
      }
    });
    this.companyForm.controls.name.clearAsyncValidators();
    this.companyForm.controls.name.setAsyncValidators(CompanyValidator.checkCompanyName(this.company.name));
  }

  getFormValidationErrors() {
    Object.keys(this.companyForm.controls).forEach(key => {
      const controlErrors: ValidationErrors = this.companyForm.get(key).errors;
      if (controlErrors != null) {
        Object.keys(controlErrors).forEach(keyError => {
          console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
        });
      }
    });
  }

  promptSubscription: Subscription;

  deleteCompany(id: string): void {
    this.promptSubscription = this._addressBookService.deleteCompany(id)
      .subscribe(
        (r) => {
          if (r) {
            this.onDeleteComplete(id);
          }
        },
        (error: any) => {
          this._errorHandler.processError(error);
        }
      );
  }

  promptOptions: PromptOptions = { header: 'Delete Company', body: '' };

  openModal() {
    this.promptOptions.body = 'Are you sure you want to delete \'' + this.company.name + '\'?';
    this._prompt.openModal(null, true, this.promptOptions)
      .subscribe(
        answerIsYes => {
          if (answerIsYes) {
            this.deleteCompany(this.company.id);
          }
        });
    return false;
  }

  onDeleteComplete(id: string): void {
    this.router.navigate(['addressbook']);
  }
}
